import { combineReducers } from "redux";
import { handleActions } from "redux-actions";
import fp from "lodash/fp";
import { INVENTORY } from "constants/action-types";
import type { IResponse } from "reducers/base-reducers";
import { fetchActions, IFetchStoreState, initialBaseState, updateStateActions } from "reducers/base-reducers";
import type { IAction } from "epics/base-epic";
import type { IInventoryMap, IReplenishment, IWarehouse } from "types/inventory";
import { withCache } from "helpers/data/with-cache";

export interface IInventoryState {
    replenishments: IFetchStoreState<IInventoryMap>;
    warehouses: IFetchStoreState<{ [id: string]: IWarehouse[] }> & { cache: { [id: string]: IWarehouse } };
}

export const inventory = combineReducers<IInventoryState>({
    replenishments: handleActions<IInventoryState["replenishments"]>(
        {
            ...fetchActions(INVENTORY.REPLENISHMENTS.FETCH),
            ...updateStateActions(INVENTORY.REPLENISHMENTS),
            [INVENTORY.REPLENISHMENTS.FETCH.SUCCESS]: (
                state: IInventoryState["replenishments"],
                { payload: list, meta }: IResponse<IReplenishment[]>,
            ) => ({
                ...state,
                payload: fp.compose(
                    fp.mergeWith((_, value) => (fp.isArray(value) ? value : undefined), state.payload), // overrides arrays of replenishments
                    fp.mapValues(fp.groupBy("sku_id")),
                    fp.groupBy("warehouse_id"),
                )(list),
                meta,
                error: null,
                isFetching: false,
            }),
            // update replenishment with a partial state only if the state exists
            [INVENTORY.REPLENISHMENTS.REPLACE]: (
                state: IInventoryState["replenishments"],
                { payload }: IAction<IReplenishment[]>,
            ) => ({
                ...state,
                payload: fp.compose(
                    fp.mergeWith(
                        (prev, value) => (!prev ? null : fp.isArray(value) ? fp.unionBy("id", value, prev) : undefined), // merges arrays only if state exists
                        state.payload,
                    ),
                    fp.mapValues(fp.groupBy("sku_id")),
                    fp.groupBy("warehouse_id"),
                )(payload),
            }),
        } as any,
        { ...initialBaseState },
    ),
    warehouses: handleActions<IInventoryState["warehouses"]>(
        {
            ...fetchActions<{ [ref: string]: IWarehouse[] }>(INVENTORY.WAREHOUSES.FETCH),
            ...updateStateActions(INVENTORY.WAREHOUSES),
            [INVENTORY.WAREHOUSES.FETCH.SUCCESS]: (
                state: IInventoryState["warehouses"],
                { payload }: IResponse<{ [ref: string]: IWarehouse[] }>,
            ): IInventoryState["warehouses"] => ({
                ...state,
                payload: { ...state.payload, ...withCache(state.cache, payload) },
                cache: state.cache,
                error: null,
                isFetching: false,
            }),
        } as any,
        { ...initialBaseState, cache: {} },
    ),
});
